import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import {
  L2CP_COLUMN_IDS,
  useInventoryDataStore,
  useInventoryFilterStore,
  useUIStore,
} from '@/stores';
import {
  useInventoryAll,
  useInventoryCount,
  useInventoryStatePolling,
  useInventoryStates,
} from '@/hooks';
import { computeFilterMetadata, transformInventoryItemToSLBPosition, getDefaultStateId } from '@/utils/inventory';
import { formatDateTimeUtc } from '@/utils/format';
import { CounterpartyReportsTab } from '@/features/counterparty-reports/pages/CounterpartyReportsPage';
import { AvailabilityTabPanel } from '../components/tabs/AvailabilityTabPanel';
import { InventoryTabPanel } from '../components/tabs/InventoryTabPanel';
import { TabNavigationBar, type InventoryTab } from '../components/tabs/TabNavigationBar';
import { mergeInventorySearch } from '@/features/inventory/router-helpers';
import { useInventoryViewBootstrap } from '@/features/inventory/hooks/use-inventory-view-bootstrap';
import { useInventoryTabSync } from '@/features/inventory/hooks/use-inventory-tab-sync';
import { TourSentinel } from '@/features/onboarding';

export const InventoryPage: React.FC = () => {
  const activeTab = useInventoryDataStore((state) => state.activeInventoryTab);
  const setActiveTab = useInventoryDataStore((state) => state.setActiveInventoryTab);
  const searchQuery = useInventoryDataStore((state) => state.searchQuery);
  const setSearchQuery = useInventoryDataStore((state) => state.setSearchQuery);

  const navigate = useNavigate();

  const setShowFilterSidebar = useUIStore((state) => state.setShowFilterSidebar);
  const locateColumnRequest = useUIStore((state) => state.locateColumnRequest);
  const setLocateColumnRequest = useUIStore((state) => state.setLocateColumnRequest);
  const setLocateHighlightedColumnIds = useUIStore((state) => state.setLocateHighlightedColumnIds);

  const inventoryFilters = useInventoryFilterStore((state) => state.inventoryFilters);
  const setFilterMetadata = useInventoryFilterStore((state) => state.setFilterMetadata);
  const setCurrentInventoryStateName = useInventoryDataStore((state) => state.setCurrentInventoryStateName);
  const setInventoryDataLoading = useInventoryDataStore((state) => state.setInventoryDataLoading);
  const setInventoryDataError = useInventoryDataStore((state) => state.setInventoryDataError);

  // URL ?tab is the source of truth for the active tab (LR-447).
  useInventoryTabSync();

  // URL ?view_id= is the source of truth: resolve/load the saved view and keep
  // the URL in sync without clobbering other routes (LR-511). See the hook.
  useInventoryViewBootstrap();

  // Tab-scoped URL params: view_id is inventory-only, cp/reportTab are
  // counterparty-reports-only. The bootstrap effect above re-populates view_id
  // on return to inventory from the persisted activeViewId.
  const handleTabChange = useCallback(
    (tab: InventoryTab) => {
      setActiveTab(tab);
      navigate({
        to: '/inventory',
        search: (prev) => {
          if (tab === 'counterparty-reports') {
            return mergeInventorySearch(prev, { tab, view_id: undefined });
          }
          if (tab === 'availability') {
            return mergeInventorySearch(undefined, { tab });
          }
          return mergeInventorySearch(undefined, { view_id: prev.view_id });
        },
        replace: true,
      });
    },
    [navigate, setActiveTab]
  );

  useEffect(() => {
    setShowFilterSidebar(true);
  }, [activeTab, setShowFilterSidebar]);

  // Clear search on first manual inventory → availability switch (not for subsequent switches)
  const prevTabRef = useRef(activeTab);
  useEffect(() => {
    if (activeTab === 'availability' && prevTabRef.current === 'inventory') {
      setSearchQuery('');
    }
    prevTabRef.current = activeTab;
  }, [activeTab, setSearchQuery]);

  // Per-tab applied-filters expanded state, persisted to localStorage
  const [inventoryFiltersExpanded, _setInventoryExpanded] = useState(
    () => localStorage.getItem('inventoryFiltersExpanded') === 'true'
  );
  const [availabilityFiltersExpanded, _setAvailabilityExpanded] = useState(
    () => localStorage.getItem('availabilityFiltersExpanded') === 'true'
  );
  const setInventoryFiltersExpanded = useCallback((v: boolean) => {
    _setInventoryExpanded(v);
    localStorage.setItem('inventoryFiltersExpanded', String(v));
  }, []);
  const setAvailabilityFiltersExpanded = useCallback((v: boolean) => {
    _setAvailabilityExpanded(v);
    localStorage.setItem('availabilityFiltersExpanded', String(v));
  }, []);

  // Locate column request: scroll + highlight, clear after 2s (works for both inventory and availability)
  useEffect(() => {
    if (!locateColumnRequest || activeTab === 'counterparty-reports') return;
    const { columnId } = locateColumnRequest;
    const columnIds =
      activeTab === 'inventory' && columnId === 'lentToCounterparties'
        ? [...L2CP_COLUMN_IDS]
        : [columnId];
    setLocateHighlightedColumnIds(columnIds);
    const el = document.querySelector(`[data-column-id="${columnId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
    const t = setTimeout(() => {
      setLocateHighlightedColumnIds(null);
      setLocateColumnRequest(null);
    }, 2000);
    return () => clearTimeout(t);
  }, [locateColumnRequest, activeTab, setLocateColumnRequest, setLocateHighlightedColumnIds]);

  // Inventory data fetch (shared by both inventory and availability tabs)
  const { data: statesData } = useInventoryStates();
  useInventoryStatePolling({ enabled: true, pollInterval: 45000 });

  // Default snapshot = the newest state (getDefaultStateId). The API returns
  // states newest-first (InventoryStateListView orders by '-created_at'), and the
  // Statement picker also defaults to statesData[0], so the calendar and the
  // loaded data always agree — including the end-of-day evening snapshot (LR-502).
  const defaultStateId = statesData ? getDefaultStateId(statesData) : undefined;
  const currentStateId = inventoryFilters.state || defaultStateId;
  const hasState = currentStateId !== undefined;

  const apiParams = useMemo(
    () => ({
      ...inventoryFilters,
      state: currentStateId,
    }),
    [inventoryFilters, currentStateId]
  );

  const { data: countData } = useInventoryCount(apiParams, hasState);
  const totalCount = countData?.count || 0;

  const {
    data: allInventoryData,
    isLoading,
    error,
  } = useInventoryAll(apiParams, totalCount, hasState && totalCount > 0);

  const allSLBPositions = useMemo(() => {
    if (!allInventoryData) return [];
    return allInventoryData.map(transformInventoryItemToSLBPosition);
  }, [allInventoryData]);

  const filterMetadata = useMemo(
    () => computeFilterMetadata(allSLBPositions, allInventoryData),
    [allSLBPositions, allInventoryData]
  );

  useEffect(() => {
    // Don't overwrite persisted metadata with the empty default while the
    // initial fetch is in flight — chip-collapse needs a usable option pool
    // on first paint after reload (LR-308).
    if (allInventoryData === undefined) return;
    setFilterMetadata(filterMetadata);
  }, [filterMetadata, setFilterMetadata, allInventoryData]);

  useEffect(() => {
    setInventoryDataLoading(isLoading);
    setInventoryDataError(!!error);
  }, [isLoading, error, setInventoryDataLoading, setInventoryDataError]);

  useEffect(() => {
    if (statesData && statesData.length > 0) {
      const activeId = inventoryFilters.state || defaultStateId;
      const currentState = statesData.find((state) => state.id === activeId);
      if (currentState) {
        // UTC timestamp + type, e.g. "2026-06-17 22:00 UTC (evening)" — snapshots
        // are stamped in UTC, so local formatting misreads the day (LR-485).
        const stateName = `${formatDateTimeUtc(currentState.created_at)} (${currentState.type})`;
        setCurrentInventoryStateName(stateName);
      }
    }
  }, [inventoryFilters.state, statesData, defaultStateId, setCurrentInventoryStateName]);

  // Lifted-up per-tab coordination state (pushed by each panel for header display)
  const [inventoryAppliedCount, setInventoryAppliedCount] = useState(0);
  const [inventorySearchResultCount, setInventorySearchResultCount] = useState(0);
  const [availabilityAppliedCount, setAvailabilityAppliedCount] = useState(0);
  const [availabilitySearchResultCount, setAvailabilitySearchResultCount] = useState(0);
  const [availabilityLoading, setAvailabilityLoading] = useState({ isLoading: false, hasError: false });

  const handleAvailabilityLoadingStateChange = useCallback(
    (state: { isLoading: boolean; hasError: boolean }) => setAvailabilityLoading(state),
    []
  );

  const appliedFiltersExpanded =
    activeTab === 'availability' ? availabilityFiltersExpanded : inventoryFiltersExpanded;
  const setAppliedFiltersExpanded =
    activeTab === 'availability' ? setAvailabilityFiltersExpanded : setInventoryFiltersExpanded;

  const searchDisabled =
    activeTab === 'counterparty-reports'
      ? false
      : activeTab === 'inventory'
      ? isLoading || !!error
      : availabilityLoading.isLoading || availabilityLoading.hasError;

  const searchResultCount =
    activeTab === 'counterparty-reports'
      ? undefined
      : activeTab === 'inventory'
      ? inventorySearchResultCount
      : availabilitySearchResultCount;

  const headerAppliedFiltersCount =
    activeTab === 'inventory'
      ? inventoryAppliedCount
      : activeTab === 'availability'
      ? availabilityAppliedCount
      : 0;

  return (
    <div className="relative flex h-[calc(100vh-0px)] flex-col">
      <TourSentinel name="inventory-overview" />
      <Card className="flex flex-1 flex-col overflow-hidden rounded-none border-none">
        <CardHeader className="flex flex-col gap-2 py-2">
          <TabNavigationBar
            activeTab={activeTab}
            onTabChange={handleTabChange}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            searchDisabled={searchDisabled}
            searchResultCount={searchResultCount}
            appliedFiltersCount={headerAppliedFiltersCount}
            appliedFiltersExpanded={appliedFiltersExpanded}
            onToggleAppliedFiltersExpanded={() => setAppliedFiltersExpanded(!appliedFiltersExpanded)}
          />
        </CardHeader>
        {activeTab === 'inventory' && (
          <InventoryTabPanel
            allSLBPositions={allSLBPositions}
            isLoading={isLoading}
            error={error}
            appliedFiltersExpanded={inventoryFiltersExpanded}
            onAppliedFiltersExpandedChange={setInventoryFiltersExpanded}
            onAppliedFiltersCountChange={setInventoryAppliedCount}
            onSearchResultCountChange={setInventorySearchResultCount}
          />
        )}
        {activeTab === 'availability' && (
          <AvailabilityTabPanel
            apiParams={apiParams}
            totalCount={totalCount}
            allInventoryData={allInventoryData}
            appliedFiltersExpanded={availabilityFiltersExpanded}
            onAppliedFiltersExpandedChange={setAvailabilityFiltersExpanded}
            onAppliedFiltersCountChange={setAvailabilityAppliedCount}
            onSearchResultCountChange={setAvailabilitySearchResultCount}
            onLoadingStateChange={handleAvailabilityLoadingStateChange}
            onSwitchToInventory={() => handleTabChange('inventory')}
          />
        )}
        {activeTab === 'counterparty-reports' && (
          <CardContent className="flex flex-1 flex-col overflow-hidden p-0 border-t border-gray-200 relative">
            <CounterpartyReportsTab searchQuery={searchQuery} />
          </CardContent>
        )}
      </Card>

      {/* Keyframes referenced by ScrollToTopButton's inline animation style. */}
      <style>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};
